import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../api';
import { useAdminAuth, hasRole } from '../auth';
import { SECTIONS, allowed } from '../nav';

/**
 * ⌘K — one box that resolves any identifier in the product to its page.
 *
 * Before this, finding a specific thing meant knowing which of eleven tabs
 * owned it: a phone number lived under Users, a SKU under Products, a domain
 * under Brands, and nothing linked to anything else. An ops panel's most
 * common action is "someone gave me an identifier, show me that thing" — it
 * should not require knowing the panel's internal filing system.
 *
 * Searches are role-scoped to match the API: user lookup is
 * operator/support/owner (product doc Section 7 — drilling into a person is a
 * deliberate, logged action), so an analyst's palette simply doesn't offer
 * it rather than offering it and 403ing.
 */
const GROUP_ORDER = ['Go to', 'Brands', 'Products', 'People'];

export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const { admin } = useAdminAuth();
  const canFindPeople = hasRole(admin, 'operator', 'support', 'owner');

  const [q, setQ] = useState('');
  const [brands, setBrands] = useState([]);
  const [remote, setRemote] = useState({ products: [], people: [] });
  const [busy, setBusy] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const reqSeq = useRef(0);

  // Brands are a small, bounded list — fetched once per open so brand
  // matching is instant and offline of the debounce.
  useEffect(() => {
    if (!open) return;
    setQ('');
    setRemote({ products: [], people: [] });
    setCursor(0);
    inputRef.current?.focus();
    let cancelled = false;
    adminApi.get('/ops/brands')
      .then(({ data }) => { if (!cancelled) setBrands(data.items || []); })
      .catch(() => { /* palette degrades to nav + remote search */ });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (!open || term.length < 2) { setRemote({ products: [], people: [] }); return; }

    const seq = ++reqSeq.current;
    setBusy(true);
    const t = setTimeout(async () => {
      const calls = [adminApi.get('/ops/products', { params: { q: term } }).catch(() => null)];
      if (canFindPeople) calls.push(adminApi.get('/users/search', { params: { q: term } }).catch(() => null));

      const [prodRes, peopleRes] = await Promise.all(calls);
      // A slow earlier request must never overwrite a newer one's results.
      if (seq !== reqSeq.current) return;
      setRemote({
        products: prodRes?.data?.items?.slice(0, 6) || [],
        people: peopleRes?.data?.items?.slice(0, 6) || [],
      });
      setBusy(false);
    }, 220);

    return () => clearTimeout(t);
  }, [q, open, canFindPeople]);

  const items = useMemo(() => {
    const term = q.trim().toLowerCase();
    const out = [];

    SECTIONS.filter((s) => allowed(s, admin?.role)).forEach((s) => {
      const tabs = s.tabs.length ? s.tabs.filter((t) => allowed(t, admin?.role)) : [{ label: s.label, path: s.path }];
      tabs.forEach((t) => {
        const label = s.tabs.length ? `${s.label} › ${t.label}` : s.label;
        if (!term || label.toLowerCase().includes(term)) {
          out.push({ group: 'Go to', id: `nav:${t.path}`, label, to: t.path });
        }
      });
    });

    brands
      .filter((b) => {
        if (!term) return false;
        const domains = (b.domains || []).join(' ').toLowerCase();
        return (b.name || '').toLowerCase().includes(term) || domains.includes(term);
      })
      .slice(0, 6)
      .forEach((b) => out.push({
        group: 'Brands',
        id: `brand:${b.brand_id}`,
        label: b.name,
        detail: (b.domains || []).join(', '),
        to: `/brand/${encodeURIComponent(b.brand_id)}`,
      }));

    remote.products.forEach((p) => out.push({
      group: 'Products',
      id: `product:${p.mira_sku}`,
      label: p.title || p.mira_sku,
      detail: `${p.brand || ''} · ${p.mira_sku}`,
      to: `/product/${encodeURIComponent(p.mira_sku)}`,
    }));

    remote.people.forEach((u) => out.push({
      group: 'People',
      id: `user:${u.user_id}`,
      label: u.name || '(no name)',
      detail: u.phone,
      to: `/user/${encodeURIComponent(u.user_id)}`,
    }));

    return out.sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));
  }, [q, brands, remote, admin]);

  useEffect(() => { setCursor(0); }, [q]);

  const go = useCallback((item) => {
    if (!item) return;
    onClose();
    navigate(item.to);
  }, [navigate, onClose]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, items.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); go(items[cursor]); }
  };

  if (!open) return null;

  let lastGroup = null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/25 p-4 pt-[12vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div role="dialog" aria-modal="true" aria-label="Search" className="w-full max-w-xl overflow-hidden rounded-xl border border-hairline bg-surface shadow-xl">
        <div className="flex items-center gap-2 border-b border-hairline px-4">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-ink-muted" aria-hidden="true">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={canFindPeople
              ? 'Phone, name, SKU, brand, domain, or a page…'
              : 'SKU, brand, domain, or a page…'}
            className="w-full bg-transparent py-3.5 text-sm text-ink outline-none placeholder:text-ink-muted"
          />
          {busy && <span className="text-[11px] text-ink-muted">…</span>}
        </div>

        <div className="max-h-80 overflow-y-auto py-1.5">
          {items.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-ink-muted">
              {q.trim().length < 2 ? 'Type at least two characters.' : 'Nothing matched.'}
            </p>
          ) : items.map((item, i) => {
            const header = item.group !== lastGroup ? item.group : null;
            lastGroup = item.group;
            return (
              <React.Fragment key={item.id}>
                {header && (
                  <div className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-ink-muted">{header}</div>
                )}
                <button
                  type="button"
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => go(item)}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-2 text-left ${i === cursor ? 'bg-raspberry-50' : 'hover:bg-surface-page'}`}
                >
                  <span className="min-w-0 truncate text-sm text-ink">{item.label}</span>
                  {item.detail && <span className="flex-shrink-0 truncate text-xs text-ink-muted">{item.detail}</span>}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        <div className="flex items-center gap-3 border-t border-hairline px-4 py-2 text-[11px] text-ink-muted">
          <span>↑↓ navigate</span><span>↵ open</span><span>esc close</span>
        </div>
      </div>
    </div>
  );
}
